import React, { useState, useEffect, useMemo } from 'react';
import { GanttTask, GanttConfig } from './types/gantt';
import { getDefaultSampleTasks } from './services/sampleData';
import { generateGanttSvg } from './utils/ganttSvgGenerator';
import {
  getActiveHost,
  insertGanttIntoHost,
  readExcelSelectedRange,
  copyChartImageToClipboard,
  downloadSvg,
  OfficeHostType,
} from './services/officeService';
import { Header } from './components/Header';
import { TableEditor } from './components/TableEditor';
import { GanttPreview } from './components/GanttPreview';
import { ThemeSelector } from './components/ThemeSelector';
import { SmartPasteModal } from './components/SmartPasteModal';
import { LoopModal } from './components/LoopModal';
import { PrivacyModal } from './components/PrivacyModal';
import { AboutTab } from './components/AboutTab';
import { Table, Palette, HelpCircle, Repeat, Info } from 'lucide-react';

export const App: React.FC = () => {
  const [tasks, setTasks] = useState<GanttTask[]>(() => {
    // 1. Check URL hash for shared Loop component state (#card=...)
    if (typeof window !== 'undefined') {
      try {
        const hash = window.location.hash;
        if (hash.includes('card=')) {
          const payloadStr = decodeURIComponent(hash.split('card=')[1] || '');
          const parsed = JSON.parse(payloadStr);
          if (parsed && Array.isArray(parsed.t) && parsed.t.length > 0) {
            return parsed.t.map((row: any, idx: number) => ({
              id: `task-${idx}-${Date.now()}`,
              activity: String(row[0] || `Activity ${idx + 1}`),
              start: String(row[1] || ''),
              end: String(row[2] || ''),
              progress: Number(row[3] ?? 100),
            }));
          }
        }
      } catch {
        // Fallback
      }
    }

    // 2. Check localStorage
    const saved = localStorage.getItem('gantt_tasks');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // If stored tasks contain old sample dates (2024 or 2017), auto-refresh to current date
          const hasOldStaticDates = parsed.some(
            (t: GanttTask) =>
              typeof t.start === 'string' &&
              (t.start.startsWith('2024-') || t.start.startsWith('2017-'))
          );
          if (!hasOldStaticDates) {
            // Strip legacy hardcoded sample colors so tasks cleanly inherit the active theme
            return parsed.map((t: GanttTask) => {
              if (
                t.color === '#0078D4' ||
                t.color === '#2B88D8' ||
                t.color === '#106EBE' ||
                t.color === '#005A9E' ||
                t.color === '#2563EB' ||
                t.color === '#0284C7'
              ) {
                const { color, ...cleanTask } = t;
                return cleanTask;
              }
              return t;
            });
          }
        }
      } catch {
        // Fallback to fresh sample data
      }
    }
    return getDefaultSampleTasks();
  });

  const [config, setConfig] = useState<GanttConfig>(() => {
    const saved = localStorage.getItem('gantt_config');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          title: 'Project Roadmap',
          timeScale: 'auto',
          themeId: 'office-blue',
          customColor: '#8B5CF6',
          showToday: true,
          showProgress: true,
          showGridlines: true,
          showBandedRows: true,
          showWeekNumbers: true,
          weekStartDay: 'monday',
          rowHeight: 44,
          chartWidth: 800,
          ...parsed,
        };
      } catch {
        // Fallback
      }
    }
    return {
      title: 'Project Roadmap',
      timeScale: 'auto',
      themeId: 'office-blue',
      customColor: '#8B5CF6',
      showToday: true,
      showProgress: true,
      showGridlines: true,
      showBandedRows: true,
      showWeekNumbers: true,
      weekStartDay: 'monday',
      rowHeight: 44,
      chartWidth: 800,
    };
  });

  const [activeTab, setActiveTab] = useState<'editor' | 'themes' | 'loop' | 'help' | 'about'>('editor');
  const [host, setHost] = useState<OfficeHostType>('Browser');
  const [isInserting, setIsInserting] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isPasteModalOpen, setIsPasteModalOpen] = useState<boolean>(false);
  const [isLoopModalOpen, setIsLoopModalOpen] = useState<boolean>(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // Check Office availability on mount
    if (typeof (window as any).Office !== 'undefined' && (window as any).Office.onReady) {
      (window as any).Office.onReady(() => {
        setHost(getActiveHost());
      });
    } else {
      setHost(getActiveHost());
    }
  }, []);

  // Save to local storage on changes
  useEffect(() => {
    localStorage.setItem('gantt_tasks', JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem('gantt_config', JSON.stringify(config));
  }, [config]);

  // Memoize SVG generation
  const renderResult = useMemo(() => {
    return generateGanttSvg(tasks, config);
  }, [tasks, config]);

  const showStatus = (msg: string, duration = 4000) => {
    setStatusMessage(msg);
    setTimeout(() => {
      setStatusMessage(null);
    }, duration);
  };

  const handleInsert = async () => {
    setIsInserting(true);
    try {
      const res = await insertGanttIntoHost(renderResult.svgString, tasks);
      showStatus(res.message);
    } catch (e: any) {
      showStatus(`Failed to insert: ${e?.message || e}`);
    } finally {
      setIsInserting(false);
    }
  };

  const handleCopyImage = async () => {
    const ok = await copyChartImageToClipboard(renderResult.svgString, 2);
    if (ok) {
      showStatus('Chart image copied! Paste directly into your slide/doc with Cmd+V / Ctrl+V.');
    } else {
      showStatus('Failed to copy image to clipboard.');
    }
  };

  const handleDownloadSvg = () => {
    downloadSvg(renderResult.svgString, `${config.title || 'gantt-chart'}.svg`);
    showStatus('Gantt chart SVG downloaded.');
  };

  const handleReloadExample = () => {
    const freshTasks = getDefaultSampleTasks();
    setTasks(freshTasks);
    showStatus('Reloaded example project with current dates!');
  };

  const handleApplyPasteTasks = (newTasks: GanttTask[]) => {
    setTasks(newTasks);
    showStatus(`Imported ${newTasks.length} tasks successfully!`);
  };

  const handleImportFromExcel = async () => {
    const result = await readExcelSelectedRange();
    if (result.error) {
      showStatus(result.error);
    } else if (result.tasks.length > 0) {
      setTasks(result.tasks);
      showStatus(`Imported ${result.tasks.length} tasks from selected Excel cells!`);
    } else {
      showStatus('No valid tasks found in selection. Ensure 3 columns: Activity, Start, End.');
    }
  };

  return (
    <div className="app-container">
      <Header
        host={host}
        onInsert={handleInsert}
        onCopyImage={handleCopyImage}
        onDownloadSvg={handleDownloadSvg}
        isInserting={isInserting}
        statusMessage={statusMessage}
      />

      <main className="main-content">
        <div className="workspace-tabs">
          <button
            className={`tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
            onClick={() => setActiveTab('editor')}
          >
            <Table size={15} />
            <span>Activities & Data</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'themes' ? 'active' : ''}`}
            onClick={() => setActiveTab('themes')}
          >
            <Palette size={15} />
            <span>Theme & Layout</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'loop' ? 'active' : ''}`}
            onClick={() => setActiveTab('loop')}
          >
            <Repeat size={15} />
            <span>Loop Component</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'help' ? 'active' : ''}`}
            onClick={() => setActiveTab('help')}
          >
            <HelpCircle size={15} />
            <span>Guide</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            <Info size={15} />
            <span>About</span>
          </button>
        </div>

        <div className="workspace-body">
          {activeTab === 'editor' && (
            <TableEditor
              tasks={tasks}
              onTasksChange={setTasks}
              onOpenPasteModal={() => setIsPasteModalOpen(true)}
              onReloadExample={handleReloadExample}
              isExcel={host === 'Excel'}
              onImportFromExcel={handleImportFromExcel}
            />
          )}

          {activeTab === 'themes' && (
            <ThemeSelector config={config} onConfigChange={setConfig} />
          )}

          {activeTab === 'loop' && (
            <div className="help-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px' }}>Microsoft Loop & Adaptive Card Integration</h3>
                  <p style={{ color: '#605E5C', fontSize: '12px', marginTop: '2px' }}>
                    Export your Gantt chart as a portable, collaborative Adaptive Card v1.5 component for Microsoft Loop, Teams, and Outlook.
                  </p>
                </div>
                <button
                  onClick={() => setIsLoopModalOpen(true)}
                  className="btn btn-primary btn-sm"
                >
                  <Repeat size={14} />
                  <span>Open Loop Component Builder</span>
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
                <div style={{ padding: '12px', border: '1px solid #E1DFDD', borderRadius: '6px', backgroundColor: '#FAF9F8' }}>
                  <strong>Live Loop Component</strong>
                  <p style={{ fontSize: '12px', color: '#605E5C', marginTop: '4px' }}>
                    Generate Adaptive Card JSON with actionable links to unfurl real-time timelines inside Loop pages and Teams channels.
                  </p>
                </div>
                <div style={{ padding: '12px', border: '1px solid #E1DFDD', borderRadius: '6px', backgroundColor: '#FAF9F8' }}>
                  <strong>Microsoft 365 App Package</strong>
                  <p style={{ fontSize: '12px', color: '#605E5C', marginTop: '4px' }}>
                    Includes pre-configured <code>appPackage/manifest.json</code> for link unfurling and Teams message extensions.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'help' && (
            <div className="help-card">
              <h3>How to use Gantt for M365</h3>
              <ol className="help-list">
                <li>
                  <strong>Input Activities:</strong> Enter your task name, start date, and end date in the table.
                </li>
                {host === 'Excel' && (
                  <li>
                    <strong>Excel Direct Read:</strong> Select any 3 columns in your worksheet and click <em>&ldquo;Read Selection&rdquo;</em>.
                  </li>
                )}
                <li>
                  <strong>Smart Paste:</strong> Click <em>Smart Paste</em> to copy data from Excel spreadsheets or Markdown tables directly into the chart.
                </li>
                <li>
                  <strong>Customize Styling:</strong> Choose from 5 corporate color themes in the <em>Theme & Layout</em> tab.
                </li>
                <li>
                  <strong>Insert into Document:</strong> Click <em>Insert to {host !== 'Browser' ? host : 'Slide'}</em> to place a crisp vector Gantt chart into your active document.
                </li>
                <li>
                  <strong>Microsoft Loop:</strong> Use the <em>Loop Component</em> tab to export Adaptive Cards for Loop workspaces and Teams.
                </li>
              </ol>
            </div>
          )}

          {activeTab === 'about' && (
            <AboutTab
              host={host}
              onOpenPrivacy={() => setIsPrivacyModalOpen(true)}
            />
          )}

          <GanttPreview renderResult={renderResult} taskCount={tasks.length} />
        </div>
      </main>

      <SmartPasteModal
        isOpen={isPasteModalOpen}
        onClose={() => setIsPasteModalOpen(false)}
        onApply={handleApplyPasteTasks}
      />

      <LoopModal
        isOpen={isLoopModalOpen}
        onClose={() => setIsLoopModalOpen(false)}
        tasks={tasks}
        config={config}
        chartDataUrl={renderResult.dataUrl}
      />

      <PrivacyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
      />
    </div>
  );
};
